import { AssetWrapperProps } from 'atoms/Asset/AssetWrapper';
import Grid from 'atoms/Layout/Grid/Grid';
import Stack from 'atoms/Layout/Stack/Stack';
import React, { useState } from 'react';
import BaseModal from 'src/app_previous/molecules/Shared/BaseModal/BaseModal';
import { prefillField } from 'src/functions/hubspot/hubspot.service';
import { useFormResultController } from 'src/hooks/useFormResultController';
import useHubspotFormCreation from 'src/hooks/useHubspotFormCreation';
import { usePopupController } from 'src/hooks/usePopupController';
import { EmailPopupCustomization } from 'src/hooks/usePopupVisibility';
import usePopupVisibility from 'src/hooks/usePopupVisibility';
import ImageBlock from './ImageBlock';

export interface EmailPopupProps {
  customization?: EmailPopupCustomization;
  image: AssetWrapperProps['imageProps'];
  formContent: (blocked: boolean, customization?: EmailPopupCustomization) => JSX.Element;
  successContent: (handleClose?: () => void) => JSX.Element;
  failureContent: (handleClose?: () => void) => JSX.Element;
  formWrapperId: string;
  formId: string;
  baseStorageKey: string;
}

const EmailPopup = ({
  customization,
  image,
  formContent,
  successContent,
  failureContent,
  formWrapperId,
  formId,
  baseStorageKey,
}: EmailPopupProps): JSX.Element => {
  const [status, setStatus] = useState<'success' | 'failure' | null>(null);
  const [isSubmitBlocked, setIsSubmitBlocked] = useState(false);

  const { isPopupVisible, handleClose } = usePopupVisibility({
    customization,
    baseStorageKey,
  });

  const { setFormSubmission } = useFormResultController();
  const { isPaused } = usePopupController();

  useHubspotFormCreation({
    formWrapperId,
    formId,
    onFormReadyCallback: () => {
      // Prefill form position field
      prefillField('hubspotEmailPopupForm', 'input[name="form_position"]', 'modal');
    },
    onFormSubmittedCallback: () => {
      setStatus('success');
      setFormSubmission();
    },
    onFormSubmitCallback: () => {
      setIsSubmitBlocked(true);
    },
    errorCallback: () => {
      const failureTimeout = setTimeout(() => {
        setStatus('failure');
        clearTimeout(failureTimeout);
      }, 2000);
    },
    dependencies: [isPaused],
  });

  const renderContent = () => {
    if (status === 'success') return successContent(handleClose);
    if (status === 'failure') return failureContent(handleClose);

    return formContent(isSubmitBlocked, customization);
  };

  const renderImageBlock = () => <ImageBlock imageProps={image} />;

  const renderContentBlock = () => (
    <Stack
      justify='center'
      padding={['spacing-7xl', 'spacing-8xl']}
      paddingMd='spacing-6xl'
      align='center'
      background='bg-primary'
      columns={6}
      columnsMd={1}
    >
      {renderContent()}
    </Stack>
  );

  // TODO (MKT-9357): Migrate the BaseModal to the new app folder
  return (
    <BaseModal
      isVisible={isPopupVisible}
      handleClose={handleClose}
      width={1016}
    >
      <Grid>
        <Stack
          borderRadius='radius-2xl'
          overflow='hidden'
          justify='center'
          align='center'
        >
          <Grid
            totalColumns={11}
            totalColumnsMd={1}
          >
            {renderImageBlock()}
            {renderContentBlock()}
          </Grid>
        </Stack>
      </Grid>
    </BaseModal>
  );
};

export default EmailPopup;
